import { useState, useEffect, useCallback } from 'react'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { func, object, bool } from 'prop-types'
import { useDispatch } from 'react-redux'
import { showSnackbar } from 'functions'
import { ConnectorCustomFields, validCustomFields } from '@components/connector-custom-fields'
import { api } from 'api'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

export const Branch = ({
  updatedAccount,
  setUpdatedAccount,
  setRequiredFieldMissing,
  newConnection,
  getHelperText,
}) => {
  const [customFields, setCustomFields] = useState([])
  const dispatch = useDispatch()

  const requiredFieldsHaveValue =
    !updatedAccount.metadata.branch_key || !updatedAccount.metadata.branch_secret

  const checkForBranchErrors = (validRequiredFields, customFields) => {
    const customFieldsAreValid = validCustomFields(customFields)
    if (!customFieldsAreValid) {
      setRequiredFieldMissing(true)
      return true
    }
    setRequiredFieldMissing(false)

    newConnection && setRequiredFieldMissing(validRequiredFields)
    return newConnection ? validRequiredFields : false
  }

  function fetchConnectorCustomFields() {
    api
      .get(`/api/v1/account/connector/${updatedAccount.id}/custom-fields`)
      .then(resp => {
        setCustomFields(resp.data)
      })
      .catch(() => {
        dispatch(
          showSnackbar({
            message: t`Unable to load custom fields for your connected Branch integration.`,
            type: 'ERROR',
          }),
        )
      })
  }

  useEffect(() => {
    checkForBranchErrors(requiredFieldsHaveValue, customFields)
  }, [requiredFieldsHaveValue, customFields])

  useEffect(() => {
    if (!newConnection) {
      fetchConnectorCustomFields()
    }
  }, [newConnection])

  useEffect(() => {
    setUpdatedAccount(prevState => ({
      ...prevState,
      customFields,
    }))
  }, [customFields])

  function handleCustomFieldDeletion(deletedField) {
    if (updatedAccount.deletedFields == undefined) {
      setUpdatedAccount(prevState => ({
        ...prevState,
        deletedFields: [deletedField],
      }))
      return
    }
    setUpdatedAccount(prevState => ({
      ...prevState,
      deletedFields: prevState.deletedFields.concat([deletedField]),
    }))
  }

  const handleBranchKeyChange = useCallback(
    value => {
      if (newConnection) {
        setUpdatedAccount(prevState => ({
          ...prevState,
          metadata: {
            ...prevState.metadata,
            branch_key: value,
          },
        }))
        return
      }
      if (value === '') {
        const newAccount = { ...updatedAccount }
        delete newAccount.metadata.branch_key
        setUpdatedAccount(newAccount)
        return
      }
      setUpdatedAccount(prevState => ({
        ...prevState,
        metadata: {
          ...prevState.metadata,
          branch_key: value,
        },
      }))
    },
    [newConnection, updatedAccount.metadata],
  )

  const handleBranchSecretChange = useCallback(
    value => {
      if (newConnection) {
        setUpdatedAccount(prevState => ({
          ...prevState,
          metadata: {
            ...prevState.metadata,
            branch_secret: value,
          },
        }))
        return
      }
      if (value === '') {
        const newAccount = { ...updatedAccount }
        delete newAccount.metadata.branch_secret
        setUpdatedAccount(newAccount)
        return
      }
      setUpdatedAccount(prevState => ({
        ...prevState,
        metadata: {
          ...prevState.metadata,
          branch_secret: value,
        },
      }))
    },
    [newConnection, updatedAccount.metadata],
  )

  return (
    <>
      <div style={{ marginBottom: 15 }}>
        <TextField
          style={{ width: '100%', marginBottom: 12 }}
          variant="outlined"
          required
          label={t`Branch key`}
          value={updatedAccount.metadata.branch_key}
          onChange={e => handleBranchKeyChange(e.target.value)}
          helperText={getHelperText(newConnection && !updatedAccount.metadata.branch_key)}
        />
        <Typography variant="caption">
          <Trans>
            Branch Keys are unique to each app within your Branch account. Keys are securely stored
            and never surfaced to the user. Entering a new value will override the existing key.
          </Trans>
        </Typography>
      </div>
      <div>
        <TextField
          style={{ width: '100%', marginBottom: 12 }}
          variant="outlined"
          required
          label={t`Branch secret`}
          type="password"
          value={updatedAccount.metadata.branch_secret}
          onChange={e => handleBranchSecretChange(e.target.value)}
          helperText={getHelperText(newConnection && !updatedAccount.metadata.branch_secret)}
        />
        <Typography variant="caption">
          <Trans>
            Branch Secrets are unique to each app within your Branch account. Secrets are securely
            stored and never surfaced to the user. Entering a new value will override the existing
            secret.
          </Trans>
        </Typography>
        <ConnectorCustomFields
          connectorId={updatedAccount.id}
          customFields={customFields}
          updateCustomFields={setCustomFields}
          updateDeletedFields={handleCustomFieldDeletion}
          connectorType="branch"
        />
      </div>
    </>
  )
}

Branch.propTypes = {
  updatedAccount: object.isRequired,
  setUpdatedAccount: func.isRequired,
  newConnection: bool.isRequired,
  setRequiredFieldMissing: func.isRequired,
  getHelperText: func.isRequired,
}
